import type {
  AdminErpDashboard,
  AdminErpInventoryItem,
  AdminErpOutboundOrder,
  AdminErpPurchaseOrder,
  AdminErpStoreOrderCandidate,
  AdminErpWarehouse,
  AdminStoreSalesOrderOption,
  AdminUser,
  PaginationMeta,
} from "../admin/api";

type ApiResponse<T> = T & {
  ok: boolean;
  message?: string;
  errors?: Record<string, string>;
};

export class ErpApiError extends Error {
  status: number;
  errors: Record<string, string>;

  constructor(message: string, status = 0, errors: Record<string, string> = {}) {
    super(message);
    this.name = "ErpApiError";
    this.status = status;
    this.errors = errors;
  }
}

const csrfKey = "fulling_erp_csrf";

type ErpRequestOptions = {
  body?: XMLHttpRequestBodyInit | null;
  headers?: Record<string, string>;
  method?: string;
};

export type UploadProgressInfo = {
  loaded: number;
  total: number;
  percent: number;
};

export function getErpCsrfToken() {
  return sessionStorage.getItem(csrfKey) || "";
}

function setErpCsrfToken(token?: string) {
  if (token) sessionStorage.setItem(csrfKey, token);
}

function clearErpCsrfToken() {
  sessionStorage.removeItem(csrfKey);
}

async function request<T>(path: string, options: ErpRequestOptions = {}) {
  const response = await fetch(path, {
    credentials: "include",
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body && !(options.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
      ...(getErpCsrfToken() ? { "X-CSRF-Token": getErpCsrfToken() } : {}),
      ...(options.headers || {}),
    },
  });
  const data = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (data && "CsrfToken" in data) {
    setErpCsrfToken(String(data.CsrfToken || ""));
  }
  if (!response.ok || data.ok === false) {
    throw new ErpApiError(data.message || "操作失敗，請稍後再試。", response.status, data.errors || {});
  }
  return data;
}

function xhrRequest<T>(path: string, options: ErpRequestOptions & { onUploadProgress?: (progress: UploadProgressInfo) => void } = {}) {
  return new Promise<ApiResponse<T>>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || "GET", path);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Accept", "application/json");
    if (options.body && !(options.body instanceof FormData)) xhr.setRequestHeader("Content-Type", "application/json");
    const csrf = getErpCsrfToken();
    if (csrf) xhr.setRequestHeader("X-CSRF-Token", csrf);
    Object.entries(options.headers || {}).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    xhr.upload.onprogress = (event) => {
      if (!options.onUploadProgress || !event.lengthComputable) return;
      options.onUploadProgress({
        loaded: event.loaded,
        total: event.total,
        percent: Math.min(100, Math.round((event.loaded / event.total) * 100)),
      });
    };
    xhr.onload = () => {
      let data = {} as ApiResponse<T>;
      try {
        data = (xhr.responseText ? JSON.parse(xhr.responseText) : {}) as ApiResponse<T>;
      } catch {
        reject(new ErpApiError("系統回應格式不正確，請稍後再試。", xhr.status));
        return;
      }
      if (data && "CsrfToken" in data) setErpCsrfToken(String(data.CsrfToken || ""));
      if (xhr.status < 200 || xhr.status >= 300 || data.ok === false) {
        reject(new ErpApiError(data.message || "操作失敗，請稍後再試。", xhr.status, data.errors || {}));
        return;
      }
      resolve(data);
    };
    xhr.onerror = () => reject(new ErpApiError("網路連線失敗，請稍後再試。", xhr.status || 0));
    xhr.onabort = () => reject(new ErpApiError("操作已取消。", xhr.status || 0));
    xhr.send(options.body || null);
  });
}

export async function fetchErpCsrf() {
  const data = await request<{ CsrfToken: string }>("/api/erp/csrf");
  setErpCsrfToken(data.CsrfToken);
  return data.CsrfToken;
}

export async function loginErp(Username: string, Password: string) {
  if (!getErpCsrfToken()) await fetchErpCsrf();
  return request<{ User: AdminUser; CsrfToken: string }>("/api/erp/login", {
    method: "POST",
    body: JSON.stringify({ Username, Password }),
  });
}

export async function getCurrentErpUser() {
  return request<{ User: AdminUser; CsrfToken: string }>("/api/erp/me");
}

export async function logoutErp() {
  const result = await request<Record<string, never>>("/api/erp/logout", { method: "POST" });
  clearErpCsrfToken();
  return result;
}

export async function getErpDashboard() {
  return request<AdminErpDashboard>("/api/admin/erp/dashboard");
}

export type ErpInventoryParams = {
  Keyword?: string;
  StockLevel?: string;
  ErpWarehouseId?: string;
  Source?: string;
  Alert?: string;
  Page?: number;
  PerPage?: number;
};

export type ErpAttachment = {
  ErpAttachmentId: string;
  ParentAttachmentId: string | null;
  RootAttachmentId?: string;
  VersionNo: number;
  RelatedType: string;
  RelatedTypeLabel: string;
  RelatedId: string | null;
  RelatedSummary?: { Title: string; Subtitle?: string | null; Path?: string | null } | null;
  AttachmentType: string;
  AttachmentTypeLabel: string;
  DocumentCategory: string;
  DocumentCategoryLabel: string;
  FileName: string;
  FileUrl: string | null;
  MimeType: string | null;
  FileSize: number | null;
  IsConfidential: boolean;
  DownloadCount: number;
  Note: string | null;
  UploadedByAdminName: string | null;
  DisabledAt?: string | null;
  DisabledReason?: string | null;
  CreatedAt: string;
  Versions?: Array<{
    ErpAttachmentId: string;
    ParentAttachmentId: string | null;
    VersionNo: number;
    FileName: string;
    FileUrl: string | null;
    IsActive: boolean;
    IsConfidential: boolean;
    DownloadCount: number;
    UploadedByAdminName?: string | null;
    CreatedAt: string;
  }>;
  AccessLogs?: Array<{
    Action: string;
    ActionLabel: string;
    AdminName: string;
    IpAddress?: string;
    CreatedAt: string;
  }>;
};

export type ErpAttachmentPayload = {
  RelatedType: string;
  RelatedId?: string;
  AttachmentType: string;
  DocumentCategory?: string;
  ParentAttachmentId?: string;
  VersionNo?: number;
  IsConfidential?: boolean;
  FileName: string;
  FileUrl?: string;
  MimeType?: string;
  FileSize?: number;
  Note?: string;
};

export type ErpPayablePayment = {
  ErpSupplierPaymentId: string;
  PaymentNumber: string;
  ErpPaymentBatchId: string | null;
  BatchNumber: string | null;
  PaymentDate: string;
  PaymentMethod: string;
  PaymentMethodLabel: string;
  Amount: number;
  ReconciliationStatus: string;
  ReconciliationStatusLabel: string;
  ReconciledAt: string | null;
  ReferenceNumber: string | null;
  BankAccountLast5: string | null;
  Status: string;
  Note: string | null;
  VoidReason: string | null;
  PaidByAdminName: string | null;
  CreatedAt: string;
};

export type ErpPaymentBatch = {
  ErpPaymentBatchId: string;
  BatchNumber: string;
  BatchDate: string;
  PaymentMethod: string;
  PaymentMethodLabel: string;
  BankAccountName: string | null;
  BankAccountLast5: string | null;
  TotalAmount: number;
  PaymentCount: number;
  Status: string;
  StatusLabel: string;
  ReconciliationStatus: string;
  ReconciliationStatusLabel: string;
  StatementNumber: string | null;
  StatementDate: string | null;
  ReconciledAt: string | null;
  ReconciledByAdminName: string | null;
  Note: string | null;
  CreatedByAdminName: string | null;
  CreatedAt: string;
};

export type ErpSupplierPayable = {
  ErpSupplierPayableId: string;
  PayableNumber: string;
  ErpSupplierId: string | null;
  SupplierCode: string | null;
  SupplierName: string;
  ErpPurchaseOrderId: string | null;
  PurchaseNumber: string | null;
  ReceiptNumber: string | null;
  InvoiceNumber: string | null;
  InvoiceDate: string | null;
  DueDate: string | null;
  Currency: string;
  Subtotal: number;
  TaxAmount: number;
  TotalAmount: number;
  PaidAmount: number;
  BalanceAmount: number;
  AgingDays: number;
  AgingBucket: string;
  AgingBucketLabel: string;
  Status: string;
  StatusLabel: string;
  Note: string | null;
  ApprovedAt: string | null;
  ApprovedByAdminName: string | null;
  CreatedByAdminName: string | null;
  CreatedAt: string;
  Payments: ErpPayablePayment[];
};

export type ErpPayablePayload = {
  ErpSupplierId?: string;
  ErpPurchaseOrderId?: string;
  ErpPurchaseReceiptId?: string;
  SupplierName?: string;
  InvoiceNumber?: string;
  InvoiceDate?: string;
  DueDate?: string;
  Currency?: string;
  ExchangeRate?: number;
  Subtotal?: number;
  TaxAmount?: number;
  TotalAmount: number;
  Note?: string;
};

export type ErpOrderParams = {
  Keyword?: string;
  Status?: string;
  ErpSupplierId?: string;
  ErpWarehouseId?: string;
  LocationId?: string;
  SourceType?: string;
  DateFrom?: string;
  DateTo?: string;
  CandidateLocationId?: string;
  Page?: number;
  PerPage?: number;
};

export type ErpStockTransferParams = {
  Keyword?: string;
  Status?: string;
  TransferPurpose?: string;
  ErpWarehouseId?: string;
  FromWarehouseId?: string;
  ToWarehouseId?: string;
  DateFrom?: string;
  DateTo?: string;
  Page?: number;
  PerPage?: number;
};

export type ErpStockMovementParams = {
  Keyword?: string;
  MovementType?: string;
  MovementGroup?: string;
  ErpWarehouseId?: string;
  DateFrom?: string;
  DateTo?: string;
  Page?: number;
  PerPage?: number;
};

export type ErpStockUnitParams = {
  Keyword?: string;
  Status?: string;
  ErpWarehouseId?: string;
  DateFrom?: string;
  DateTo?: string;
  Page?: number;
  PerPage?: number;
};

export type ErpStockAdjustmentPayload = {
  ErpWarehouseId: string;
  AdjustmentType: string;
  AdjustmentDate?: string;
  Reason: string;
  Note?: string;
  RequiresReview?: boolean;
  WorkflowStatus?: "Draft" | "Counting" | "PendingReview";
  Items: Array<{
    ProductId: string;
    VariantId?: string | null;
    Quantity: number;
    UnitCost?: number;
    EarSide?: string;
    Serials?: Array<{ SerialNumber?: string; EarSide?: string; Note?: string }>;
    StockUnitIds?: string[];
    Note?: string;
  }>;
};

export type ErpStockAdjustmentOrderItem = {
  ErpStockAdjustmentOrderItemId: string;
  ProductId: string;
  VariantId: string | null;
  Sku: string | null;
  ProductName: string;
  VariantLabel: string | null;
  Quantity: number;
  QuantityDelta: number;
  BeforeQuantity: number;
  AfterQuantity: number;
  UnitCost: number;
  EarSide: string | null;
  EarSideLabel: string;
  SerialText: string | null;
  Note: string | null;
};

export type ErpStockAdjustmentOrder = {
  ErpStockAdjustmentOrderId: string;
  AdjustmentNumber: string;
  ErpWarehouseId: string;
  WarehouseName: string;
  AdjustmentType: string;
  AdjustmentTypeLabel: string;
  AdjustmentDate: string;
  Reason: string;
  Status: string;
  StatusLabel: string;
  IsReviewRequired: boolean;
  ItemCount: number;
  QuantityIn: number;
  QuantityOut: number;
  Items: ErpStockAdjustmentOrderItem[];
  Note: string | null;
  CreatedByAdminName: string | null;
  ReviewedAt: string | null;
  ReviewedByAdminName: string | null;
  ReviewNote: string | null;
  CreatedAt: string;
};

export type ErpStockAdjustmentUserOption = {
  AdminUserId: string;
  DisplayName: string;
  Username: string;
};

export type ErpStockMovement = {
  ErpStockMovementId: string;
  MovementType: string;
  MovementTypeLabel: string;
  WarehouseName: string | null;
  ProductId: string;
  VariantId: string | null;
  ProductName: string;
  VariantLabel: string | null;
  Sku: string | null;
  QuantityDelta: number;
  BeforeQuantity: number;
  AfterQuantity: number;
  StoreOrderNumber: string | null;
  PurchaseNumber: string | null;
  OutboundNumber: string | null;
  Note: string | null;
  AdminUserName: string | null;
  CreatedAt: string;
};

export type ErpStockUnit = {
  ErpStockUnitId: string;
  WarehouseName: string | null;
  ProductName: string;
  VariantLabel: string | null;
  Sku: string | null;
  SerialNumber: string | null;
  EarSide: string | null;
  EarSideLabel: string;
  Quantity: number;
  UnitCost: number;
  Status: string;
  StatusLabel: string;
  StoreOrderNumber: string | null;
  LocationName: string | null;
  ReceivedAt: string | null;
  OutboundAt: string | null;
  Note: string | null;
  CreatedAt: string;
  UpdatedAt: string;
};

export type ErpStockTransferItem = {
  ErpStockTransferOrderItemId: string;
  ProductId: string;
  VariantId: string | null;
  Sku: string | null;
  ProductName: string;
  VariantLabel: string | null;
  Quantity: number;
  UnitCost: number;
  SerialText: string | null;
  Note: string | null;
};

export type ErpStockTransferOrder = {
  ErpStockTransferOrderId: string;
  TransferNumber: string;
  TransferPurpose: string;
  TransferPurposeLabel: string;
  FromWarehouseId: string;
  FromWarehouseName: string;
  ToWarehouseId: string;
  ToWarehouseName: string;
  Status: string;
  StatusLabel: string;
  ItemCount: number;
  QuantityTotal: number;
  Items: ErpStockTransferItem[];
  Note: string | null;
  CreatedByAdminName: string | null;
  CreatedAt: string;
  ShippedAt: string | null;
  ReceivedAt: string | null;
};

export type ErpStockTransferPayload = {
  TransferPurpose?: string;
  FromWarehouseId: string;
  ToWarehouseId: string;
  Note?: string;
  Items: Array<{ ProductId: string; VariantId?: string | null; Quantity: number; UnitCost?: number; SerialText?: string; Note?: string }>;
};

export type ErpPurchaseOrderItem = {
  ErpPurchaseOrderItemId: string;
  ProductId: string;
  VariantId: string | null;
  Sku: string | null;
  ProductName: string;
  VariantLabel: string | null;
  Quantity: number;
  UnitCost: number;
  ArrivedQuantity?: number;
  ReceivedQuantity: number;
  RemainingQuantity: number;
  Note: string | null;
};

export type ErpPurchaseReceipt = {
  ErpPurchaseReceiptId: string;
  ReceiptNumber: string;
  ReceivedAt: string;
  WarehouseName: string | null;
  QuantityTotal: number;
  InspectionStatus?: string;
  InspectionStatusLabel?: string;
  ArrivedQuantity?: number;
  AcceptedQuantity?: number;
  RejectedQuantity?: number;
  ReleasedAt?: string | null;
  ReleaseNote?: string | null;
  ExceptionSummary?: string | null;
  ResolutionAction?: string | null;
  ResolutionActionLabel?: string | null;
  RmaNumber?: string | null;
  RmaStatus?: string | null;
  RmaStatusLabel?: string | null;
  SupplierContactNote?: string | null;
  ReturnTrackingNumber?: string | null;
  ReplacementExpectedDate?: string | null;
  ReplacementReceivedAt?: string | null;
  CreditAmount?: number;
  Note: string | null;
};

export type ErpPurchaseReceiptItem = {
  ErpPurchaseReceiptItemId: string;
  ErpStockUnitId?: string | null;
  ProductId: string;
  VariantId: string | null;
  Sku: string | null;
  ProductName: string;
  VariantLabel: string | null;
  SerialNumber: string | null;
  EarSide: string | null;
  EarSideLabel: string;
  Quantity: number;
  ArrivedQuantity: number;
  AcceptedQuantity: number;
  ReleasedQuantity?: number;
  RejectedQuantity: number;
  QuarantinedQuantity?: number;
  UnitCost: number;
  InspectionStatus: string;
  InspectionStatusLabel: string;
  ExceptionType: string | null;
  ExceptionTypeLabel: string | null;
  ResolutionAction: string | null;
  ResolutionActionLabel: string | null;
  ExceptionStatus: string;
  ExceptionStatusLabel: string;
  InspectionNote: string | null;
  ResolutionNote: string | null;
  ResolvedAt: string | null;
  Checklist: Record<string, boolean>;
  Note: string | null;
  CreatedAt: string;
};

export type ErpPurchaseReceiptRmaEvent = {
  ErpPurchaseReceiptRmaEventId: string;
  EventType: string;
  EventLabel: string;
  RmaStatus: string;
  RmaStatusLabel: string;
  SupplierContactNote: string | null;
  ReturnTrackingNumber: string | null;
  ReplacementExpectedDate: string | null;
  ReplacementReceivedAt: string | null;
  CreditAmount: number;
  Note: string | null;
  CreatedByAdminName: string | null;
  CreatedAt: string;
};

export type ErpPurchaseReceiptRecord = ErpPurchaseReceipt & {
  ErpPurchaseOrderId: string;
  PurchaseNumber: string;
  SupplierName: string | null;
  SupplierCode: string | null;
  WarehouseId: string | null;
  InspectionStatus: string;
  InspectionStatusLabel: string;
  ArrivedQuantity: number;
  AcceptedQuantity: number;
  RejectedQuantity: number;
  ReleasedAt?: string | null;
  ReleaseNote?: string | null;
  ExceptionStatus: string;
  ExceptionStatusLabel: string;
  ExceptionSummary: string | null;
  ResolutionAction: string | null;
  ResolutionActionLabel: string | null;
  ResolutionNote: string | null;
  RmaNumber: string | null;
  RmaStatus: string | null;
  RmaStatusLabel: string | null;
  SupplierContactNote: string | null;
  ReturnTrackingNumber: string | null;
  ReplacementExpectedDate: string | null;
  ReplacementReceivedAt: string | null;
  CreditAmount: number;
  ResolvedAt: string | null;
  CreatedByAdminName: string | null;
  CreatedAt: string;
  Items?: ErpPurchaseReceiptItem[];
  RmaEvents?: ErpPurchaseReceiptRmaEvent[];
};

export type ErpSupplierOption = {
  ErpSupplierId: string;
  Code: string;
  Name: string;
  LeadTimeDays: number;
  PaymentTerms: string | null;
  Currency?: string;
  MinimumOrderAmount?: number;
};

export type ErpPurchaseReceiveItem = {
  ErpPurchaseOrderItemId: string;
  ReceiveQuantity: number;
  AcceptedQuantity?: number;
  UnitCost: number;
  InspectionStatus?: string;
  ExceptionType?: string;
  ResolutionAction?: string;
  InspectionNote?: string;
  InspectionChecklist?: Record<string, boolean>;
  Serials?: Array<{ SerialNumber?: string; EarSide?: string; Note?: string }>;
};

export type ErpProductSearchItem = {
  ProductId: string;
  VariantId: string | null;
  ProductCode: string;
  ProductName: string;
  VariantLabel: string | null;
  Sku: string;
  SalePrice: number;
  AvailableStock: number;
  SupplierPrice: {
    ErpSupplierProductPriceId: string;
    SupplierSku: string | null;
    PurchaseUnit: string;
    UnitCost: number;
    Currency: string;
    MinimumOrderQuantity: number;
    LeadTimeDays: number;
  } | null;
};

export type ErpInventoryCountCandidate = ErpProductSearchItem & {
  CategoryId?: string | null;
  CategoryName?: string | null;
  ErpInStock: number;
  PendingInspectionStock: number;
  QuarantinedStock: number;
  SafetyStock: number;
  ReorderPoint: number;
  NeedsReorder: boolean;
  StockStatusLabel: string;
  SuggestedDifferenceQuantity: number;
};

function queryString(params: Record<string, string | number | boolean | null | undefined>) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "" && value !== false) query.set(key, String(value));
  });
  return query.toString() ? `?${query.toString()}` : "";
}

async function downloadFile(path: string, fallbackFilename: string) {
  const response = await fetch(path, {
    credentials: "include",
    headers: {
      Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ...(getErpCsrfToken() ? { "X-CSRF-Token": getErpCsrfToken() } : {}),
    },
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ErpApiError(data?.message || "匯出失敗，請稍後再試。", response.status);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const utf8Name = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const fallbackName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const filename = utf8Name ? decodeURIComponent(utf8Name) : fallbackName || fallbackFilename;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function listErpInventory(params: ErpInventoryParams = {}) {
  return request<{
    Items: AdminErpInventoryItem[];
    Counts: { Total: number; Available: number; Low: number; Out: number; Reorder?: number; Incoming?: number; Reserved?: number; StockValue?: number; PendingInspection?: number; Quarantined?: number };
    Pagination: PaginationMeta;
    Warehouses: AdminErpWarehouse[];
    StockLevelOptions: AdminStoreSalesOrderOption[];
    SourceOptions: AdminStoreSalesOrderOption[];
    AlertOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/erp/inventory${queryString(params)}`);
}

export async function exportErpInventory(params: ErpInventoryParams = {}) {
  return downloadFile(`/api/admin/erp/inventory/export${queryString(params)}`, `ERP-Inventory-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export type ErpInventoryWarehouseStock = {
  ErpWarehouseId: string;
  WarehouseName: string;
  WarehouseTypeLabel: string;
  InStock: number;
  Reserved: number;
  Outbound: number;
  PendingInspectionStock?: number;
  QuarantinedStock?: number;
  AvailableStock: number;
  IncomingStock: number;
  PurchaseIncoming?: number;
  TransferIncoming?: number;
  SafetyStock: number;
  ReorderPoint: number;
  NeedsReorder: boolean;
  AverageCost: number;
  StockValue: number;
};

export type ErpInventoryOpenOrder = {
  Number: string;
  Status: string;
  StatusLabel: string;
  SupplierName?: string | null;
  LocationName?: string | null;
  FromWarehouseName?: string | null;
  ToWarehouseName?: string | null;
  TransferPurposeLabel?: string | null;
  ExpectedArrivalDate?: string | null;
  Quantity: number;
  ReceivedQuantity?: number;
  RemainingQuantity?: number;
  UnitCost?: number;
  ShippedAt?: string | null;
  CreatedAt: string;
};

export type ErpInventoryDetail = {
  Item: AdminErpInventoryItem;
  Policy: { SafetyStock: number; ReorderPoint: number; MaxStock: number; LeadTimeDays: number; Note?: string | null };
  WarehouseStocks: ErpInventoryWarehouseStock[];
  StockUnits: ErpStockUnit[];
  Movements: ErpStockMovement[];
  OpenPurchaseOrders: ErpInventoryOpenOrder[];
  OpenOutboundOrders: ErpInventoryOpenOrder[];
  OpenTransfers: ErpInventoryOpenOrder[];
};

export type ErpInventoryPolicyPayload = {
  VariantId?: string | null;
  ErpWarehouseId?: string | null;
  SafetyStock: number;
  ReorderPoint: number;
  MaxStock: number;
  LeadTimeDays: number;
  Note?: string;
  IsActive: boolean;
};

export async function getErpInventoryDetail(ProductId: string, VariantId?: string | null) {
  return request<ErpInventoryDetail>(`/api/admin/erp/inventory/${ProductId}${queryString({ VariantId: VariantId || undefined })}`);
}

export async function updateErpInventoryPolicy(ProductId: string, payload: ErpInventoryPolicyPayload) {
  return request<{
    Item: AdminErpInventoryItem | null;
    Policy: ErpInventoryDetail["Policy"];
    WarehouseStocks: ErpInventoryWarehouseStock[];
  }>(`/api/admin/erp/inventory/${ProductId}/policy`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function listErpAttachments(params: { Keyword?: string; RelatedType?: string; RelatedId?: string; AttachmentType?: string; DocumentCategory?: string; ConfidentialOnly?: boolean; IncludeDisabled?: boolean; DateFrom?: string; DateTo?: string; Page?: number; PerPage?: number } = {}) {
  return request<{
    Items: ErpAttachment[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    RelatedTypeOptions: AdminStoreSalesOrderOption[];
    AttachmentTypeOptions: AdminStoreSalesOrderOption[];
    DocumentCategoryOptions: AdminStoreSalesOrderOption[];
    CanViewConfidential?: boolean;
  }>(`/api/admin/erp/attachments${queryString(params)}`);
}

export async function createErpAttachment(payload: ErpAttachmentPayload) {
  return request<Record<string, never>>("/api/admin/erp/attachments", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function uploadErpAttachment(payload: ErpAttachmentPayload, file: File, onUploadProgress?: (progress: UploadProgressInfo) => void) {
  const formData = new FormData();
  formData.append("File", file);
  formData.append("RelatedType", payload.RelatedType);
  if (payload.RelatedId) formData.append("RelatedId", payload.RelatedId);
  formData.append("AttachmentType", payload.AttachmentType);
  if (payload.DocumentCategory) formData.append("DocumentCategory", payload.DocumentCategory);
  if (payload.ParentAttachmentId) formData.append("ParentAttachmentId", payload.ParentAttachmentId);
  if (payload.VersionNo) formData.append("VersionNo", String(payload.VersionNo));
  if (payload.IsConfidential) formData.append("IsConfidential", "1");
  if (payload.FileName) formData.append("FileName", payload.FileName);
  if (payload.Note) formData.append("Note", payload.Note);
  return xhrRequest<{ Item: ErpAttachment }>("/api/admin/erp/attachments/upload", {
    method: "POST",
    body: formData,
    onUploadProgress,
  });
}

export async function downloadErpAttachment(ErpAttachmentId: string) {
  return request<{ DownloadUrl: string }>(`/api/admin/erp/attachments/${ErpAttachmentId}/download`);
}

export async function disableErpAttachment(ErpAttachmentId: string, payload: { DisabledReason: string }) {
  return request<Record<string, never>>(`/api/admin/erp/attachments/${ErpAttachmentId}/disable`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listErpStockMovements(params: ErpStockMovementParams = {}) {
  return request<{
    Items: ErpStockMovement[];
    Pagination: PaginationMeta;
    MovementTypeOptions: AdminStoreSalesOrderOption[];
    Warehouses: ErpWarehouse[];
  }>(`/api/admin/erp/stock-movements${queryString(params)}`);
}

export async function listErpStockUnits(params: ErpStockUnitParams = {}) {
  return request<{
    Items: ErpStockUnit[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    StatusOptions: AdminStoreSalesOrderOption[];
    Warehouses: ErpWarehouse[];
  }>(`/api/admin/erp/stock-units${queryString(params)}`);
}

export async function getErpStockAdjustmentOptions() {
  return request<{
    Warehouses: ErpWarehouse[];
    AdjustmentTypeOptions: AdminStoreSalesOrderOption[];
  }>("/api/admin/erp/stock-adjustments/options");
}

export async function listErpStockAdjustments(params: { Keyword?: string; AdjustmentType?: string; Status?: string; CreatedByAdminUserId?: string; ErpWarehouseId?: string; DateFrom?: string; DateTo?: string; AdjustmentGroup?: string; RequiresReview?: string; Page?: number; PerPage?: number } = {}) {
  return request<{
    Orders: ErpStockAdjustmentOrder[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    AdjustmentTypeOptions: AdminStoreSalesOrderOption[];
    StatusOptions: AdminStoreSalesOrderOption[];
    CreatedByOptions: ErpStockAdjustmentUserOption[];
    Warehouses: ErpWarehouse[];
  }>(`/api/admin/erp/stock-adjustments${queryString(params)}`);
}

export async function getErpStockAdjustment(ErpStockAdjustmentOrderId: string) {
  return request<{
    Order: ErpStockAdjustmentOrder;
    AdjustmentTypeOptions: AdminStoreSalesOrderOption[];
    StatusOptions: AdminStoreSalesOrderOption[];
    Warehouses: ErpWarehouse[];
  }>(`/api/admin/erp/stock-adjustments/${ErpStockAdjustmentOrderId}`);
}

export async function createErpStockAdjustment(payload: ErpStockAdjustmentPayload) {
  return request<{ message?: string }>("/api/admin/erp/stock-adjustments", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function approveErpStockAdjustment(ErpStockAdjustmentOrderId: string, payload: { ReviewNote?: string } = {}) {
  return request<{ message?: string }>(`/api/admin/erp/stock-adjustments/${ErpStockAdjustmentOrderId}/approve`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function rejectErpStockAdjustment(ErpStockAdjustmentOrderId: string, payload: { ReviewNote?: string } = {}) {
  return request<{ message?: string }>(`/api/admin/erp/stock-adjustments/${ErpStockAdjustmentOrderId}/reject`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function startErpStockAdjustment(ErpStockAdjustmentOrderId: string, payload: { ReviewNote?: string } = {}) {
  return request<{ message?: string }>(`/api/admin/erp/stock-adjustments/${ErpStockAdjustmentOrderId}/start`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function submitErpStockAdjustment(ErpStockAdjustmentOrderId: string, payload: { ReviewNote?: string } = {}) {
  return request<{ message?: string }>(`/api/admin/erp/stock-adjustments/${ErpStockAdjustmentOrderId}/submit`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function cancelErpStockAdjustment(ErpStockAdjustmentOrderId: string, payload: { ReviewNote?: string } = {}) {
  return request<{ message?: string }>(`/api/admin/erp/stock-adjustments/${ErpStockAdjustmentOrderId}/cancel`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listErpStockTransfers(params: ErpStockTransferParams = {}) {
  return request<{
    Orders: ErpStockTransferOrder[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    StatusOptions: AdminStoreSalesOrderOption[];
    TransferPurposeOptions: AdminStoreSalesOrderOption[];
    Warehouses: ErpWarehouse[];
  }>(`/api/admin/erp/stock-transfers${queryString(params)}`);
}

export async function createErpStockTransfer(payload: ErpStockTransferPayload) {
  return request<Record<string, never>>("/api/admin/erp/stock-transfers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function shipErpStockTransfer(ErpStockTransferOrderId: string) {
  return request<Record<string, never>>(`/api/admin/erp/stock-transfers/${ErpStockTransferOrderId}/ship`, { method: "POST" });
}

export async function receiveErpStockTransfer(ErpStockTransferOrderId: string) {
  return request<Record<string, never>>(`/api/admin/erp/stock-transfers/${ErpStockTransferOrderId}/receive`, { method: "POST" });
}

export async function searchErpProducts(params: { Keyword?: string; ErpSupplierId?: string; ProductId?: string; VariantId?: string | null }) {
  return request<{ Items: ErpProductSearchItem[] }>(`/api/admin/erp/products/search${queryString(params)}`);
}

export async function listErpInventoryCountCandidates(params: { Keyword?: string; ErpWarehouseId?: string; ProductCategoryId?: string; StockStatus?: string; IncludeZeroStock?: boolean; Limit?: number } = {}) {
  return request<{
    Items: ErpInventoryCountCandidate[];
    Counts: Record<string, number>;
    Categories: Array<{ ProductCategoryId: string; Name: string }>;
    StockStatusOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/erp/inventory-count-candidates${queryString(params)}`);
}

export type ErpPurchaseSupplierSuggestion = {
  ErpSupplierProductPriceId?: string;
  ErpSupplierId: string;
  SupplierCode?: string | null;
  SupplierName?: string | null;
  SupplierSku?: string | null;
  PurchaseUnit: string;
  UnitCost: number;
  Currency: string;
  MinimumOrderQuantity: number;
  LeadTimeDays: number;
  PaymentTerms?: string | null;
  IsDefault?: boolean;
  RecommendationReason?: string | null;
};

export type ErpPurchaseDemandGroup = {
  ProductId: string;
  VariantId: string | null;
  ProductName: string;
  VariantLabel?: string | null;
  Sku?: string | null;
  DemandQuantity: number;
  IncomingQuantity: number;
  SuggestedPurchaseQuantity: number;
  StoreOrderCount: number;
  SourceOrderIds: string[];
  SourceOrders: Array<{ StoreSalesOrderId: string; OrderNumber?: string | null; CustomerName?: string | null; LocationName?: string | null; OrderPurpose?: string | null; OrderPurposeLabel?: string | null; ZeroPriceReason?: string | null; ZeroPriceReasonLabel?: string | null; ZeroPriceNote?: string | null }>;
  LocationNames: string[];
  LocationSummary?: string;
  EarliestOrderAt?: string | null;
  SupplierPriceOptions: ErpPurchaseSupplierSuggestion[];
  RecommendedSupplier?: ErpPurchaseSupplierSuggestion | null;
  SupplierOptionCount?: number;
  SuggestedReasons?: string[];
  PriceStatus: "HasSupplierPrice" | "MissingSupplierPrice";
};

export async function listErpPurchaseOrders(params: ErpOrderParams = {}) {
  return request<{
    Orders: AdminErpPurchaseOrder[];
    Candidates: AdminErpStoreOrderCandidate[];
    DemandGroups: ErpPurchaseDemandGroup[];
    SupplierOptions: ErpSupplierOption[];
    WarehouseOptions: ErpWarehouse[];
    LocationOptions: ErpLocationOption[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    StatusOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/erp/purchase-orders${queryString(params)}`);
}

export async function createErpPurchaseOrder(payload: {
  StoreSalesOrderId: string;
  StoreSalesOrderIds?: string[];
  ErpSupplierId?: string;
  SupplierName?: string;
  ExternalOrderNumber?: string;
  Currency?: string;
  ExchangeRate?: number;
  ExpectedArrivalDate?: string;
  Note?: string;
  Items?: Array<{ LineKey?: string; StoreSalesOrderItemId?: string; ProductId?: string; VariantId?: string | null; Quantity: number; UnitCost: number; SupplierSku?: string; PurchaseUnit?: string; Note?: string }>;
}) {
  return request<{ Order: AdminErpPurchaseOrder | null }>("/api/admin/erp/purchase-orders", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getErpPurchaseOrder(ErpPurchaseOrderId: string) {
  return request<{ Order: AdminErpPurchaseOrder | null }>(`/api/admin/erp/purchase-orders/${ErpPurchaseOrderId}`);
}

export async function approveErpPurchaseOrder(ErpPurchaseOrderId: string, payload: { ApprovalNote?: string } = {}) {
  return request<{ Order: AdminErpPurchaseOrder | null }>(`/api/admin/erp/purchase-orders/${ErpPurchaseOrderId}/approve`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function cancelErpPurchaseOrder(ErpPurchaseOrderId: string, payload: { CancelReason: string }) {
  return request<{ Order: AdminErpPurchaseOrder | null }>(`/api/admin/erp/purchase-orders/${ErpPurchaseOrderId}/cancel`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function reviseErpPurchaseOrder(ErpPurchaseOrderId: string, payload: {
  ErpSupplierId?: string;
  SupplierName?: string;
  ExternalOrderNumber?: string;
  Currency?: string;
  ExchangeRate?: number;
  ExpectedArrivalDate?: string;
  Note?: string;
  RevisionNote: string;
  Items: Array<{ LineKey?: string; ProductId?: string; VariantId?: string | null; Quantity: number; UnitCost: number; SupplierSku?: string; PurchaseUnit?: string; Note?: string }>;
}) {
  return request<{ Order: AdminErpPurchaseOrder | null }>(`/api/admin/erp/purchase-orders/${ErpPurchaseOrderId}/revision`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function receiveErpPurchaseOrder(ErpPurchaseOrderId: string, payload: { WarehouseId?: string; Note?: string; Items: ErpPurchaseReceiveItem[] }) {
  return request<Record<string, never>>(`/api/admin/erp/purchase-orders/${ErpPurchaseOrderId}/receive`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listErpPurchaseReceipts(params: ErpOrderParams & { InspectionStatus?: string; ExceptionStatus?: string } = {}) {
  return request<{
    Receipts: ErpPurchaseReceiptRecord[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    InspectionStatusOptions: AdminStoreSalesOrderOption[];
    ExceptionStatusOptions: AdminStoreSalesOrderOption[];
    ResolutionActionOptions: AdminStoreSalesOrderOption[];
    SupplierOptions: ErpSupplierOption[];
    WarehouseOptions: ErpWarehouse[];
  }>(`/api/admin/erp/purchase-receipts${queryString(params)}`);
}

export async function getErpPurchaseReceipt(ErpPurchaseReceiptId: string) {
  return request<{
    Receipt: ErpPurchaseReceiptRecord | null;
    ResolutionActionOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/erp/purchase-receipts/${ErpPurchaseReceiptId}`);
}

export async function releaseErpPurchaseReceipt(ErpPurchaseReceiptId: string, payload: {
  Mode: "AcceptAll" | "QuarantineAll";
  Note?: string;
  InspectionChecklist?: Record<string, boolean>;
  ExceptionType?: string;
  ResolutionAction?: string;
}) {
  return request<{ Receipt: ErpPurchaseReceiptRecord | null }>(`/api/admin/erp/purchase-receipts/${ErpPurchaseReceiptId}/release`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function resolveErpPurchaseReceipt(ErpPurchaseReceiptId: string, payload: {
  ResolutionAction: string;
  ResolutionNote: string;
  RmaNumber?: string;
  RmaStatus?: string;
  SupplierContactNote?: string;
  ReturnTrackingNumber?: string;
  ReplacementExpectedDate?: string;
  ReplacementReceivedAt?: string;
  CreditAmount?: number;
}) {
  return request<{ Receipt: ErpPurchaseReceiptRecord | null }>(`/api/admin/erp/purchase-receipts/${ErpPurchaseReceiptId}/resolve`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listErpPayables(params: ErpOrderParams = {}) {
  return request<{
    Items: ErpSupplierPayable[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    StatusOptions: AdminStoreSalesOrderOption[];
    PaymentMethodOptions: AdminStoreSalesOrderOption[];
    SupplierOptions: ErpSupplierOption[];
    PurchaseOptions: Array<{ ErpPurchaseOrderId: string; PurchaseNumber: string; SupplierName: string | null; PurchaseTotal: number }>;
  }>(`/api/admin/erp/payables${queryString(params)}`);
}

export async function createErpPayable(payload: ErpPayablePayload) {
  return request<Record<string, never>>("/api/admin/erp/payables", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function approveErpPayable(ErpSupplierPayableId: string) {
  return request<Record<string, never>>(`/api/admin/erp/payables/${ErpSupplierPayableId}/approve`, { method: "POST" });
}

export async function createErpPayablePayment(ErpSupplierPayableId: string, payload: { PaymentDate?: string; PaymentMethod: string; Amount: number; ReferenceNumber?: string; BankAccountLast5?: string; Note?: string }) {
  return request<Record<string, never>>(`/api/admin/erp/payables/${ErpSupplierPayableId}/payments`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function voidErpPayablePayment(ErpSupplierPaymentId: string, payload: { VoidReason: string }) {
  return request<Record<string, never>>(`/api/admin/erp/payments/${ErpSupplierPaymentId}/void`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listErpPaymentBatches(params: { Keyword?: string; Status?: string; ReconciliationStatus?: string; DateFrom?: string; DateTo?: string; Page?: number; PerPage?: number } = {}) {
  return request<{
    Items: ErpPaymentBatch[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    StatusOptions: AdminStoreSalesOrderOption[];
    ReconciliationStatusOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/erp/payment-batches${queryString(params)}`);
}

export async function createErpPaymentBatch(payload: { PaymentIds: string[]; BatchDate?: string; PaymentMethod: string; BankAccountName?: string; BankAccountLast5?: string; Note?: string }) {
  return request<Record<string, never>>("/api/admin/erp/payment-batches", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function reconcileErpPaymentBatch(ErpPaymentBatchId: string, payload: { StatementNumber: string; StatementDate?: string; Note?: string }) {
  return request<Record<string, never>>(`/api/admin/erp/payment-batches/${ErpPaymentBatchId}/reconcile`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listErpOutboundOrders(params: ErpOrderParams = {}) {
  return request<{
    Orders: AdminErpOutboundOrder[];
    Candidates: AdminErpStoreOrderCandidate[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    StatusOptions: AdminStoreSalesOrderOption[];
    Warehouses: AdminErpWarehouse[];
    LocationOptions: ErpLocationOption[];
    LogisticsCarrierOptions: ErpLogisticsCarrierOption[];
  }>(`/api/admin/erp/outbound-orders${queryString(params)}`);
}

export type ErpLogisticsCarrierOption = {
  LogisticsCarrierId: string;
  Code: string;
  Name: string;
  ContactName?: string | null;
  ContactPhone?: string | null;
  TrackingUrlTemplate?: string | null;
  Note?: string | null;
  SortOrder?: number;
};

export async function createErpOutboundOrder(payload: {
  StoreSalesOrderId: string;
  FromWarehouseId?: string;
  Note?: string;
  InspectionStatus?: "Passed" | "Abnormal" | "Blocked" | string;
  InspectionChecklist?: Record<string, boolean>;
  AbnormalNote?: string;
  ResolutionNote?: string;
  PickingChecklist?: Record<string, boolean>;
  PackingChecklist?: Record<string, boolean>;
  LogisticsCarrier?: string;
  TrackingNumber?: string;
  LogisticsNote?: string;
  Items?: Array<{ StoreSalesOrderItemId: string; ErpStockUnitIds: string[] }>;
}) {
  return request<Record<string, never>>("/api/admin/erp/outbound-orders", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateErpOutboundLogistics(ErpOutboundOrderId: string, payload: { PickingChecklist?: Record<string, boolean>; PackingChecklist?: Record<string, boolean>; LogisticsCarrier?: string; TrackingNumber?: string; LogisticsNote?: string }) {
  return request<Record<string, never>>(`/api/admin/erp/outbound-orders/${ErpOutboundOrderId}/logistics`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function shipErpOutboundOrder(ErpOutboundOrderId: string, payload: { PickingChecklist?: Record<string, boolean>; PackingChecklist?: Record<string, boolean>; LogisticsCarrier?: string; TrackingNumber?: string; LogisticsNote?: string } = {}) {
  return request<Record<string, never>>(`/api/admin/erp/outbound-orders/${ErpOutboundOrderId}/ship`, { method: "POST", body: JSON.stringify(payload) });
}

export async function arriveErpOutboundOrder(ErpOutboundOrderId: string) {
  return request<Record<string, never>>(`/api/admin/erp/outbound-orders/${ErpOutboundOrderId}/arrive`, { method: "POST" });
}

export async function storeSignErpOutboundOrder(ErpOutboundOrderId: string, payload: { StoreSignedNote?: string }) {
  return request<Record<string, never>>(`/api/admin/erp/outbound-orders/${ErpOutboundOrderId}/store-sign`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type ErpQualityInspection = {
  ErpOutboundOrderId: string;
  OutboundNumber: string;
  StoreSalesOrderId: string | null;
  StoreOrderNumber: string;
  CustomerName: string;
  FromWarehouseName: string;
  ToLocationName: string;
  Status: string;
  StatusLabel: string;
  InspectionNumber: string;
  InspectionStatus: string;
  InspectionStatusLabel: string;
  Checklist: Record<string, boolean>;
  AbnormalNote: string;
  ResolutionNote: string;
  InspectedAt: string | null;
  CreatedByAdminName: string;
  ItemCount: number;
  QuantityTotal: number;
  CreatedAt: string;
};

export type ErpAccountingPeriod = {
  ErpAccountingPeriodId: string;
  PeriodCode: string;
  PeriodName: string;
  DateFrom: string;
  DateTo: string;
  Status: string;
  StatusLabel: string;
  LockInventory: boolean;
  LockPurchase: boolean;
  LockPayment: boolean;
  LockReason: string | null;
  ClosedAt: string | null;
  ClosedByAdminName: string | null;
  CreatedAt: string;
  UpdatedAt: string;
};

export type ErpRmaOrder = {
  ErpRmaOrderId: string;
  RmaOrderNumber: string;
  ErpPurchaseReceiptId: string | null;
  ErpSupplierId: string | null;
  SupplierName: string | null;
  RmaType: string;
  RmaTypeLabel: string;
  Status: string;
  StatusLabel: string;
  RmaNumber: string | null;
  ReturnTrackingNumber: string | null;
  ReturnedAt?: string | null;
  ReturnCarrier?: string | null;
  ReplacementExpectedDate: string | null;
  ReplacementReceivedAt: string | null;
  CreditAmount: number;
  CreditRequestedAt?: string | null;
  WriteOffAt?: string | null;
  WriteOffAmount?: number;
  IssueSummary: string | null;
  ResolutionNote: string | null;
  StageNote?: string | null;
  ItemCount: number;
  Items: Array<Record<string, unknown>>;
  ClosedAt: string | null;
  CreatedAt: string;
  UpdatedAt: string;
};

export type ErpImportBatch = {
  ErpImportBatchId: string;
  ImportNumber: string;
  ImportType: string;
  ImportTypeLabel: string;
  Status: string;
  StatusLabel: string;
  FileName: string | null;
  TotalRows: number;
  ValidRows: number;
  ErrorRows: number;
  AppliedRows: number;
  ErrorSummary: Array<{ Row?: number; Message?: string }>;
  Note: string | null;
  AppliedAt: string | null;
  CreatedAt: string;
};

export type ErpNotificationTask = {
  ErpNotificationTaskId: string;
  TaskType: string;
  Priority: string;
  PriorityLabel: string;
  Status: string;
  StatusLabel: string;
  Title: string;
  Message: string | null;
  RelatedType: string | null;
  RelatedId: string | null;
  RelatedNumber: string | null;
  DueDate: string | null;
  CompletedAt: string | null;
  CompletionNote: string | null;
  CreatedAt: string;
};

export async function listErpAccountingPeriods(params: { Keyword?: string; Status?: string } = {}) {
  return request<{ Items: ErpAccountingPeriod[]; Counts: Record<string, number>; StatusOptions: AdminStoreSalesOrderOption[] }>(`/api/admin/erp/accounting-periods${queryString(params)}`);
}

export async function createErpAccountingPeriod(payload: { PeriodCode: string; PeriodName: string; DateFrom: string; DateTo: string; LockInventory?: boolean; LockPurchase?: boolean; LockPayment?: boolean; LockReason?: string }) {
  return request<Record<string, never>>("/api/admin/erp/accounting-periods", { method: "POST", body: JSON.stringify(payload) });
}

export async function closeErpAccountingPeriod(ErpAccountingPeriodId: string, payload: { LockInventory?: boolean; LockPurchase?: boolean; LockPayment?: boolean; LockReason?: string } = {}) {
  return request<Record<string, never>>(`/api/admin/erp/accounting-periods/${ErpAccountingPeriodId}/close`, { method: "POST", body: JSON.stringify(payload) });
}

export async function reopenErpAccountingPeriod(ErpAccountingPeriodId: string, payload: { ReopenReason?: string } = {}) {
  return request<Record<string, never>>(`/api/admin/erp/accounting-periods/${ErpAccountingPeriodId}/reopen`, { method: "POST", body: JSON.stringify(payload) });
}

export async function voidErpAccountingPeriod(ErpAccountingPeriodId: string, payload: { VoidReason: string }) {
  return request<Record<string, never>>(`/api/admin/erp/accounting-periods/${ErpAccountingPeriodId}/void`, { method: "POST", body: JSON.stringify(payload) });
}

export async function listErpRmaOrders(params: { Keyword?: string; Status?: string } = {}) {
  return request<{ Items: ErpRmaOrder[]; Counts: Record<string, number>; StatusOptions: AdminStoreSalesOrderOption[]; TypeOptions: AdminStoreSalesOrderOption[]; SupplierOptions: ErpSupplierOption[]; LogisticsCarrierOptions: ErpLogisticsCarrierOption[] }>(`/api/admin/erp/rma-orders${queryString(params)}`);
}

export async function createErpRmaOrder(payload: { RmaType: string; ErpSupplierId?: string; SupplierName?: string; ErpPurchaseReceiptId?: string; RmaNumber?: string; ReturnTrackingNumber?: string; ReplacementExpectedDate?: string; CreditAmount?: number; IssueSummary: string; ResolutionNote?: string }) {
  return request<Record<string, never>>("/api/admin/erp/rma-orders", { method: "POST", body: JSON.stringify(payload) });
}

export async function closeErpRmaOrder(ErpRmaOrderId: string, payload: { ResolutionNote?: string } = {}) {
  return request<Record<string, never>>(`/api/admin/erp/rma-orders/${ErpRmaOrderId}/close`, { method: "POST", body: JSON.stringify(payload) });
}

export async function updateErpRmaOrderStage(ErpRmaOrderId: string, payload: { Status: string; ReturnCarrier?: string; ReturnTrackingNumber?: string; ReplacementReceivedAt?: string; CreditAmount?: number; WriteOffAmount?: number; StageNote?: string }) {
  return request<Record<string, never>>(`/api/admin/erp/rma-orders/${ErpRmaOrderId}/stage`, { method: "POST", body: JSON.stringify(payload) });
}

export async function listErpImportBatches(params: { Keyword?: string; ImportType?: string } = {}) {
  return request<{ Items: ErpImportBatch[]; Counts: Record<string, number>; ImportTypeOptions: AdminStoreSalesOrderOption[]; StatusOptions: AdminStoreSalesOrderOption[] }>(`/api/admin/erp/import-batches${queryString(params)}`);
}

export async function validateErpImportBatch(payload: { ImportType: string; FileName?: string; Content: string; Note?: string }) {
  return request<Record<string, never>>("/api/admin/erp/import-batches/validate", { method: "POST", body: JSON.stringify(payload) });
}

export async function applyErpImportBatch(ErpImportBatchId: string) {
  return request<Record<string, never>>(`/api/admin/erp/import-batches/${ErpImportBatchId}/apply`, { method: "POST" });
}

export async function listErpTasks(params: { Keyword?: string; Status?: string } = {}) {
  return request<{ Items: ErpNotificationTask[]; Counts: Record<string, number>; StatusOptions: AdminStoreSalesOrderOption[]; PriorityOptions: AdminStoreSalesOrderOption[] }>(`/api/admin/erp/tasks${queryString(params)}`);
}

export async function completeErpTask(ErpNotificationTaskId: string, payload: { CompletionNote?: string } = {}) {
  return request<Record<string, never>>(`/api/admin/erp/tasks/${ErpNotificationTaskId}/complete`, { method: "POST", body: JSON.stringify(payload) });
}

export type ErpReportRow = Record<string, string | number | boolean | null>;

export type ErpReportData = {
  DateFrom: string;
  DateTo: string;
  Warehouses: ErpWarehouse[];
  Metrics: {
    InventoryItems: number;
    AvailableStock: number;
    NeedsReorder: number;
    StockValue: number;
    PurchaseOrders: number;
    PurchaseTotal: number;
    ReceiptExceptions: number;
    OutboundOrders: number;
    StockMovements: number;
    InventoryTurnoverRate?: number;
    SlowMovingItems?: number;
    PurchaseLeadTimeRate?: number;
    SupplierExceptionRate?: number;
    ReceiptDefectRate?: number;
    PayableBalance?: number;
  };
  ReorderItems: ErpReportRow[];
  QualityRows: ErpQualityInspection[];
  ReceiptRows: ErpReportRow[];
  MovementRows: ErpReportRow[];
  TurnoverRows?: ErpReportRow[];
  SlowMovingRows?: ErpReportRow[];
  PurchaseLeadTimeRows?: ErpReportRow[];
  SupplierExceptionRows?: ErpReportRow[];
  ReceiptDefectRows?: ErpReportRow[];
  StockValueRows?: ErpReportRow[];
  PayableAgingRows?: ErpReportRow[];
};

export type ErpAuditLog = {
  ErpAuditLogId: string;
  Module: string;
  ModuleLabel: string;
  EntityType: string;
  EntityId: string | null;
  EntityNumber: string | null;
  Action: string;
  ActionLabel: string;
  Summary: string;
  BeforeData: Record<string, unknown> | null;
  AfterData: Record<string, unknown> | null;
  AdminUserId: string | null;
  AdminUsername: string;
  AdminDisplayName: string;
  IpAddress: string;
  UserAgent: string;
  CreatedAt: string;
};

export type ErpAuditOption = { Key: string; Label: string };
export type ErpAuditAdminUserOption = { AdminUserId: string; Name: string };

export async function listErpQualityInspections(params: ErpOrderParams & { InspectionStatus?: string } = {}) {
  return request<{
    Inspections: ErpQualityInspection[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    InspectionStatusOptions: AdminStoreSalesOrderOption[];
    Warehouses: AdminErpWarehouse[];
    LocationOptions: ErpLocationOption[];
  }>(`/api/admin/erp/quality-inspections${queryString(params)}`);
}

export async function getErpReports(params: { DateFrom?: string; DateTo?: string; ErpWarehouseId?: string } = {}) {
  return request<ErpReportData>(`/api/admin/erp/reports${queryString(params)}`);
}

export async function exportErpReports(params: { DateFrom?: string; DateTo?: string; ErpWarehouseId?: string } = {}) {
  return downloadFile(`/api/admin/erp/reports/export${queryString(params)}`, `ERP-Report-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export async function listErpAuditLogs(params: { Keyword?: string; Module?: string; Action?: string; AdminUserId?: string; DateFrom?: string; DateTo?: string; Page?: number; PerPage?: number } = {}) {
  return request<{
    Items: ErpAuditLog[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    ModuleOptions: ErpAuditOption[];
    ActionOptions: ErpAuditOption[];
    AdminUsers: ErpAuditAdminUserOption[];
  }>(`/api/admin/erp/audit-logs${queryString(params)}`);
}

export type ErpWarehouse = {
  ErpWarehouseId: string;
  Code: string;
  Name: string;
  WarehouseType: string;
  WarehouseTypeLabel: string;
  LocationId: string | null;
  LocationName: string | null;
  IsActive: boolean;
  SortOrder: number;
  CreatedAt: string | null;
  UpdatedAt: string | null;
};

export type ErpLocationOption = {
  LocationId: string;
  Name: string;
  IsActive: boolean;
};

export type ErpWarehousePayload = {
  Code: string;
  Name: string;
  WarehouseType: string;
  LocationId?: string | null;
  IsActive: boolean;
  SortOrder: number;
};

export async function listErpWarehouses() {
  return request<{
    Items: ErpWarehouse[];
    LocationOptions: ErpLocationOption[];
    WarehouseTypeOptions: AdminStoreSalesOrderOption[];
  }>("/api/admin/erp/warehouses");
}

export async function createErpWarehouse(payload: ErpWarehousePayload) {
  return request<{ Item: ErpWarehouse }>("/api/admin/erp/warehouses", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateErpWarehouse(ErpWarehouseId: string, payload: ErpWarehousePayload) {
  return request<{ Item: ErpWarehouse }>(`/api/admin/erp/warehouses/${ErpWarehouseId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export type ErpSupplier = {
  ErpSupplierId: string;
  Code: string;
  Name: string;
  SupplierType: string;
  SupplierTypeLabel: string;
  ShortName: string | null;
  ContactName: string | null;
  ContactTitle: string | null;
  Mobile: string | null;
  Phone: string | null;
  Fax: string | null;
  LineId: string | null;
  Email: string | null;
  Website: string | null;
  PurchaseEmail: string | null;
  FinanceEmail: string | null;
  Country: string | null;
  TaxNumber: string | null;
  Address: string | null;
  InvoiceTitle: string | null;
  InvoiceTaxId: string | null;
  BankName: string | null;
  BankAccountName: string | null;
  BankAccountNumber: string | null;
  PaymentTerms: string | null;
  Currency: string;
  MinimumOrderAmount: number;
  ShippingMethod: string | null;
  TradeTerms: string | null;
  WarrantyPolicy: string | null;
  ReturnPolicy: string | null;
  QualityRating: number;
  RiskLevel: string;
  RiskLevelLabel: string;
  Tags: string | null;
  LeadTimeDays: number;
  Note: string | null;
  IsActive: boolean;
  SortOrder: number;
  CreatedAt: string | null;
  UpdatedAt: string | null;
};

export type ErpSupplierPayload = {
  Code: string;
  Name: string;
  SupplierType?: string;
  ShortName?: string;
  ContactName?: string;
  ContactTitle?: string;
  Mobile?: string;
  Phone?: string;
  Fax?: string;
  LineId?: string;
  Email?: string;
  Website?: string;
  PurchaseEmail?: string;
  FinanceEmail?: string;
  Country?: string;
  TaxNumber?: string;
  Address?: string;
  InvoiceTitle?: string;
  InvoiceTaxId?: string;
  BankName?: string;
  BankAccountName?: string;
  BankAccountNumber?: string;
  PaymentTerms?: string;
  Currency?: string;
  MinimumOrderAmount?: number;
  ShippingMethod?: string;
  TradeTerms?: string;
  WarrantyPolicy?: string;
  ReturnPolicy?: string;
  QualityRating?: number;
  RiskLevel?: string;
  Tags?: string;
  LeadTimeDays: number;
  Note?: string;
  IsActive: boolean;
  SortOrder: number;
};

export type ErpSupplierProductPrice = {
  ErpSupplierProductPriceId: string;
  ErpSupplierId: string;
  ProductId: string;
  VariantId: string | null;
  ProductCode: string | null;
  ProductName: string;
  VariantLabel: string | null;
  Sku: string | null;
  SupplierSku: string | null;
  PurchaseUnit: string;
  UnitCost: number;
  Currency: string;
  MinimumOrderQuantity: number;
  LeadTimeDays: number;
  IsDefault: boolean;
  EffectiveFrom: string | null;
  EffectiveTo: string | null;
  IsActive: boolean;
  Note: string | null;
  UpdatedAt: string | null;
};

export type ErpSupplierPricePayload = {
  ProductId: string;
  VariantId?: string | null;
  SupplierSku?: string;
  PurchaseUnit: string;
  UnitCost: number;
  Currency: string;
  MinimumOrderQuantity: number;
  LeadTimeDays: number;
  IsDefault: boolean;
  EffectiveFrom?: string;
  EffectiveTo?: string;
  IsActive: boolean;
  Note?: string;
};

export async function listErpSuppliers(params: { Keyword?: string } = {}) {
  return request<{ Items: ErpSupplier[] }>(`/api/admin/erp/suppliers${queryString(params)}`);
}

export async function createErpSupplier(payload: ErpSupplierPayload) {
  return request<{ Item: ErpSupplier }>("/api/admin/erp/suppliers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateErpSupplier(ErpSupplierId: string, payload: ErpSupplierPayload) {
  return request<{ Item: ErpSupplier }>(`/api/admin/erp/suppliers/${ErpSupplierId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function listErpSupplierPrices(ErpSupplierId: string) {
  return request<{ Items: ErpSupplierProductPrice[] }>(`/api/admin/erp/suppliers/${ErpSupplierId}/prices`);
}

export async function createErpSupplierPrice(ErpSupplierId: string, payload: ErpSupplierPricePayload) {
  return request<{ Items: ErpSupplierProductPrice[] }>(`/api/admin/erp/suppliers/${ErpSupplierId}/prices`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateErpSupplierPrice(ErpSupplierProductPriceId: string, payload: ErpSupplierPricePayload) {
  return request<{ Items: ErpSupplierProductPrice[] }>(`/api/admin/erp/supplier-prices/${ErpSupplierProductPriceId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}
